import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType } from '../../../types/shipment';
import ShipmentDetails from '../ShipmentDetails';
import { useTheme, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import ShipmentSummary from '../ShipmentSummary';
import ShipmentSuccess from '../ShipmentSuccess';
import { Heading } from '../ShipmentSummary/styles';
import ShipperConsignee from '../ShipperConsignee';
import BatchReviewStep from '../BatchShipment/BatchReviewStep';
import BatchServicesStep from '../BatchShipment/BatchServicesStep';
import Loader from '../../../components/Loader';
import { AppDispatch, RootStateType } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { redirectToSuccessPage, resetShipmentError } from '../../../store/reducers/Shipment';
import ErrorModal from '../../../components/Modal/error';
import { resetGetRatesError } from '@Reducers/GetRates';
import NoCSPFound from '../../GetRatesV2/CSPList/NoCSPFound/NoCSPFound';
import { deleteDraftShipment, setDraftShipmentId } from '@Reducers/DraftShipment';
import dayjs from 'dayjs';
import { getRates } from '../../../store/reducers/RateSummary';
import { getServiceList } from '../../../store/reducers/CarrierAndService';
import { setSelectedUser, setReduxState } from '../../../store/reducers/AccountLookup';
import { RateObj } from '../../../store/reducers/RateSummary/Type';
import { BulkShipmentType } from '../../../types/shipment';
import { getBatchExtractionStatus, formatInvalidFieldsReason } from '../UserDetails/services/extractionService';
import { convertJobToBulkShipment } from '../BatchShipment/utils/validation';

interface StepItem {
  label: string;
  subtitle: string;
}

const stepsConfig: StepItem[] = [
  { label: 'Shipper & Consignee', subtitle: 'Sender & Recipient' },
  { label: 'Shipment Details & Services', subtitle: 'Packages & Rates' },
  { label: 'Summary', subtitle: 'Review & Confirmation' },
];

const batchStepsConfig: StepItem[] = [
  { label: 'Review Shipments', subtitle: 'Verify Extracted Invoices' },
  { label: 'Logistics & Services', subtitle: 'Dispatch & Carrier Rates' },
  { label: 'Summary', subtitle: 'Review & Confirmation' },
];

function ShipmentStepper({ modalId }: { modalId: string }) {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const {
    savedAddress: { loading: addressLoading },
    getRates: { loading: ratesLoading },
    DraftShipment: { loading: draftShipmentLoading, selectedDraftShipmentId: draftRefId },
    shipment: {
      loading: shipmentLoading,
      redirectToSuccess,
      error: { isError, msg: errorDetails },
    },
  } = useSelector((state: RootStateType) => state);

  const { loading: accountLookupLoading = false, selectedUser } = useSelector(
    (state: RootStateType) => state.accountLookup[modalId] || {},
  );
  const { loading: userDataByIdLoading } = useSelector((state: RootStateType) => state.user);
  const { profile } = useSelector((state: RootStateType) => state.loginUser);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};
  const { countryList } = useSelector((state: RootStateType) => state.global);

  const loading =
    addressLoading ||
    ratesLoading ||
    shipmentLoading ||
    accountLookupLoading ||
    userDataByIdLoading ||
    draftShipmentLoading;

  const {
    activeStep,
    setActiveStep,
    setShowRates,
    isBatchMode,
    batchId,
    setBulkShipments,
    setBatchCounts,
    batchPickupType,
    setRateAssignService,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;

  React.useEffect(() => {
    if (redirectToSuccess) {
      dispatch(setDraftShipmentId({ id: null }));
      setActiveStep(3); // Move to Step 3 (Success)
      dispatch(redirectToSuccessPage(false));
      if (draftRefId) {
        dispatch(
          deleteDraftShipment({
            payload: { deleteId: [draftRefId] },
            queryParams: { currentPage: 1, perPage: 0 },
            showModal: false,
          }),
        );
      }
    }
  }, [redirectToSuccess, draftRefId]);

  React.useEffect(() => {
    return () => {
      dispatch(setDraftShipmentId({ id: null }));
    };
  }, []);

  // 1. Batch Extraction Polling
  const activeBatchPollingRef = React.useRef<{ cancelled: boolean } | null>(null);
  const batchPollingTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (!isBatchMode || !batchId) {
      if (activeBatchPollingRef.current) {
        activeBatchPollingRef.current.cancelled = true;
        activeBatchPollingRef.current = null;
      }
      if (batchPollingTimerRef.current) {
        clearTimeout(batchPollingTimerRef.current);
        batchPollingTimerRef.current = null;
      }
      return;
    }

    const cancelRef = { cancelled: false };
    activeBatchPollingRef.current = cancelRef;

    const updateShipmentsFromJobs = (jobs: any[]) => {
      setBulkShipments((prev: BulkShipmentType[]) => {
        const prevMap = new Map(prev.map((s: BulkShipmentType) => [s.jobId, s]));

        return jobs.map((job: any) => {
          const reviewReason = formatInvalidFieldsReason(job.invalidFields);
          const errorMessage = typeof job.error === 'string' ? job.error : job.error?.message || null;

          if (prevMap.has(job.jobId)) {
            const existing = prevMap.get(job.jobId)!;
            const isNewlyCompleted =
              (existing.status === 'pending' || existing.status === 'processing') &&
              (job.status === 'completed' || job.status === 'needs_review');

            if (isNewlyCompleted) {
              const mapped = convertJobToBulkShipment(job, countryList);
              return {
                ...mapped,
                jobId: job.jobId,
                status: job.status,
                error: errorMessage,
                confidence: job.confidence,
                invalidFields: job.invalidFields,
                reviewReason,
              };
            }

            return {
              ...existing,
              status: job.status,
              error: errorMessage,
              confidence: job.confidence,
              invalidFields: job.invalidFields,
              reviewReason: reviewReason || existing.reviewReason,
            };
          }

          const mapped = convertJobToBulkShipment(job, countryList);
          return {
            ...mapped,
            jobId: job.jobId,
            status: job.status,
            error: errorMessage,
            confidence: job.confidence,
            invalidFields: job.invalidFields,
            reviewReason,
          };
        });
      });
    };

    const pollTick = async () => {
      if (cancelRef.cancelled) return;
      try {
        const statusRes = await getBatchExtractionStatus(batchId);
        if (cancelRef.cancelled) return;

        setBatchCounts(statusRes.counts || { pending: 0, completed: 0, failed: 0, needs_review: 0 });
        if (statusRes.jobs) {
          updateShipmentsFromJobs(statusRes.jobs);
        }

        if (statusRes.counts && statusRes.counts.pending > 0) {
          batchPollingTimerRef.current = setTimeout(pollTick, 15000);
        }
      } catch (err: any) {
        console.error('❌ Batch Extraction Polling Failed:', err);
        if (!cancelRef.cancelled) {
          batchPollingTimerRef.current = setTimeout(pollTick, 15000);
        }
      }
    };

    pollTick();

    return () => {
      cancelRef.cancelled = true;
      if (batchPollingTimerRef.current) {
        clearTimeout(batchPollingTimerRef.current);
        batchPollingTimerRef.current = null;
      }
    };
  }, [isBatchMode, batchId, countryList]);

  // 2. Service Provider List API
  React.useEffect(() => {
    if (isBatchMode) {
      dispatch(getServiceList({}));
    }
  }, [isBatchMode, dispatch]);

  // 3. Shipment Rates API
  const fetchBatchRates = React.useCallback(
    async (systemId?: string[]) => {
      try {
        const systemValue: string[] =
          name === 'Cargo Solutions Network' ? systemId || [] : selectedUser?.systems || profile?._systems || [];

        const accountNumber =
          selectedUser?.accountNumber || selectedUser?._accountId?.accountNumber || profile?.accountNumber || '';

        const importRes = await dispatch(
          getRates({
            navigatedDate: batchPickupType.date?.toISOString() || dayjs().toISOString(),
            accountNumber,
            pageType: 'contract',
            csp: [],
            rateType: [],
            system: systemValue,
            isImport: false,
          }),
        );

        const exportRes = await dispatch(
          getRates({
            navigatedDate: batchPickupType.date?.toISOString() || dayjs().toISOString(),
            accountNumber,
            pageType: 'contract',
            csp: [],
            rateType: [],
            system: systemValue,
            isImport: true,
          }),
        );

        let importList: RateObj[] = [];
        let exportList: RateObj[] = [];

        if (importRes.type.endsWith('/fulfilled') && importRes.payload && 'data' in importRes.payload) {
          importList = (importRes.payload as any).data.list;
        }

        if (exportRes.type.endsWith('/fulfilled') && exportRes.payload && 'data' in exportRes.payload) {
          exportList = (exportRes.payload as any).data.list;
        }

        setRateAssignService([...importList, ...exportList]);
      } catch (error) {
        console.error('Error fetching batch rates:', error);
      }
    },
    [name, selectedUser, profile, batchPickupType.date, dispatch, setRateAssignService],
  );

  React.useEffect(() => {
    if (isBatchMode && name !== 'Cargo Solutions Network') {
      fetchBatchRates();
    }
  }, [isBatchMode, name, fetchBatchRates]);

  React.useEffect(() => {
    if (isBatchMode && selectedUser) {
      dispatch(setReduxState({ modalId: 'createBulkShipment' }));
      dispatch(setSelectedUser(['createBulkShipment', selectedUser]));
    }
  }, [isBatchMode, selectedUser, dispatch]);

  const getActiveStep = (stepNumber: number) => {
    switch (stepNumber) {
      case 0:
        return isBatchMode ? (
          <BatchReviewStep />
        ) : (
          <ShipperConsignee
            userId={selectedUser?._id}
            accountNumber={selectedUser?._accountId?.accountNumber}
            accountId={selectedUser?._accountId?._id}
          />
        );
      case 1:
        return isBatchMode ? <BatchServicesStep modalId={modalId} /> : <ShipmentDetails />;
      case 2:
        return <ShipmentSummary />;
      case 3:
        return <ShipmentSuccess modalId={modalId} />;
      default:
        return <></>;
    }
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleStepClick = (index: number) => {
    // Allow jumping to completed earlier steps
    if (index < activeStep) {
      setActiveStep(index);
    }
  };

  const ohandleErrorModalClose = () => {
    dispatch(resetShipmentError());
    dispatch(resetGetRatesError());
  };

  const currentStepsConfig = isBatchMode ? batchStepsConfig : stepsConfig;

  return (
    <Loader loading={loading} overly>
      <>
        <Box sx={{ width: '100%', scrollMargin: '95px' }}>
          {activeStep < 3 && <Heading>Create Shipment</Heading>}

          {/* Modern 3-Step Stepper Header */}
          {activeStep < 3 && (
            <Box sx={{ width: '100%', my: { xs: 2, md: 3 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5, md: 3 },
                  borderRadius: '16px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 10px -2px rgba(0, 0, 0, 0.03)',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    position: 'relative',
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Background Connector Track Line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: { xs: 16, sm: 20, md: 22 },
                      left: '16.66%',
                      right: '16.66%',
                      height: 3,
                      bgcolor: alpha(theme.palette.divider, 0.12),
                      zIndex: 0,
                      borderRadius: '2px',
                    }}
                  >
                    {/* Active Progress Fill */}
                    <Box
                      sx={{
                        height: '100%',
                        width: activeStep === 0 ? '0%' : activeStep === 1 ? '50%' : '100%',
                        bgcolor: theme.palette.primary.main,
                        borderRadius: '2px',
                        transition: 'width 0.4s ease-in-out',
                      }}
                    />
                  </Box>

                  {currentStepsConfig.map((step, index) => {
                    const isCompleted = index < activeStep;
                    const isActive = index === activeStep;
                    const isPending = index > activeStep;

                    return (
                      <Box
                        key={index}
                        onClick={() => handleStepClick(index)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          position: 'relative',
                          zIndex: 1,
                          cursor: isCompleted ? 'pointer' : 'default',
                          px: { xs: 0.5, sm: 1 },
                          transition: 'all 0.2s ease',
                          '&:hover': isCompleted
                            ? {
                                transform: 'translateY(-1px)',
                              }
                            : {},
                        }}
                      >
                        {/* Step Number / Checkmark Badge */}
                        <Box
                          sx={{
                            width: { xs: 32, sm: 40, md: 44 },
                            height: { xs: 32, sm: 40, md: 44 },
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: { xs: '0.8125rem', sm: '0.9375rem', md: '1rem' },
                            transition: 'all 0.25s ease',
                            bgcolor: theme.palette.background.paper,
                            mb: { xs: 0.75, sm: 1.25 },
                            ...(isCompleted && {
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.common.white,
                              border: `2px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                            }),
                            ...(isActive && {
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.common.white,
                              border: `2px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}, 0 4px 12px ${alpha(
                                theme.palette.primary.main,
                                0.25,
                              )}`,
                            }),
                            ...(isPending && {
                              bgcolor: theme.palette.background.paper,
                              color: '#667085',
                              border: `1.5px solid ${alpha(theme.palette.divider, 0.25)}`,
                            }),
                          }}
                        >
                          {isCompleted ? <CheckIcon sx={{ fontSize: { xs: 18, sm: 22, md: 24 } }} /> : index + 1}
                        </Box>

                        {/* Step Tag */}
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: 600,
                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            mb: 0.5,
                            color: isActive ? 'primary.main' : 'text.secondary',
                            transition: 'color 0.2s ease',
                          }}
                        >
                          Step {index + 1}
                        </Typography>

                        {/* Step Title / Label */}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isActive ? 600 : 500,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                            lineHeight: { xs: 1.3, sm: 1.35 },
                            color: isActive ? 'text.primary' : 'text.secondary',
                            maxWidth: { xs: '110px', sm: '180px', md: '220px' },
                            mx: 'auto',
                            wordBreak: 'break-word',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {step.label}
                        </Typography>

                        {/* Subtitle (visible on sm and up) */}
                        <Typography
                          variant="caption"
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            color: 'text.secondary',
                            fontSize: { sm: '0.75rem', md: '0.8125rem' },
                            fontWeight: 400,
                            mt: 0.5,
                            lineHeight: 1.3,
                          }}
                        >
                          {step.subtitle}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Box>
          )}

          {/* Step Views */}
          {activeStep > 3 ? (
            <React.Fragment>
              <Typography sx={{ mt: 2, mb: 1 }}>All steps completed</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Box sx={{ flex: '1 1 auto' }} />
                <Button onClick={handleReset}>Reset</Button>
              </Box>
            </React.Fragment>
          ) : (
            <React.Fragment>{getActiveStep(activeStep)}</React.Fragment>
          )}
        </Box>

        <ErrorModal
          icon={'/images/Boxes/WarningContrast.svg'}
          open={isError || false}
          title={'Oops!!'}
          subTitle={errorDetails}
          onClose={ohandleErrorModalClose}
          onConfirm={ohandleErrorModalClose}
          btnText="Go back"
        />
        <NoCSPFound setShowRates={setShowRates} />
      </>
    </Loader>
  );
}

export default ShipmentStepper;
